import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUp, ArrowDown, Wifi, WifiOff, Trophy, Smartphone } from "lucide-react";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";
import { getRoom, wsBase } from "@/lib/api";
import ChatPanel from "@/components/ChatPanel";
import { POWERUP_DEFS, drawPowerupIcon } from "@/lib/powerups";
import { drawArenaBackground, drawPaddle as drawPaddleMirror, drawBall } from "@/lib/arena";

// Must match host canvas dimensions in PongGame.jsx
const W = 1000;
const H = 600;
const PADDLE_W = 14;
const BALL_R = 9;
const POWERUP_R = 20;

const Controller = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [name, setName] = useState("");
  const [hostName, setHostName] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [roomMeta, setRoomMeta] = useState(null);
  const [score, setScore] = useState({ p1: 0, p2: 0 });
  const [matchStarted, setMatchStarted] = useState(false);
  const [winner, setWinner] = useState(null);
  const [showRotateHint, setShowRotateHint] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);

  const wsRef = useRef(null);
  const lastDirRef = useRef("stop");
  const localInputRef = useRef(0); // -1 up, 0 stop, 1 down — for client-side P2 paddle prediction
  const lastTickAtRef = useRef(Date.now());
  const ctrlBottomRef = useRef(null);
  const tickRef = useRef({
    p1y: H / 2 - 55, p1h: 110,
    p2y: H / 2 - 55, p2h: 110,
    p1a: false, p2a: false,
    bs: [{ x: W / 2, y: H / 2, s: 1 }],
    pu: null, run: false,
  });
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  const sendInput = (action) => {
    if (lastDirRef.current === action) return;
    lastDirRef.current = action;
    // Update local-prediction direction so the mirrored P2 paddle starts
    // moving immediately on key/touch press, without waiting for the
    // network round-trip + next host tick. Authoritative position from
    // the host's tick stream resets the prediction baseline.
    localInputRef.current = action === "up" ? -1 : action === "down" ? 1 : 0;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "input", action }));
    }
  };

  const sendChat = (text) => {
    const safe = String(text || "");
    if (!safe.trim()) return;
    const myName = (sessionRef.current.name || name.trim().slice(0, 20) || "Player 2");
    setChatMessages((prev) => [
      ...prev.slice(-199),
      { from: "controller", name: myName, avatar: "", text: safe, ts: Date.now() },
    ]);
    const payload = { type: "chat", from: "controller", name: myName, avatar: "", text: safe };
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify(payload)); } catch { outboundQueueRef.current.push(payload); }
    } else {
      // WS isn't open (likely reconnecting) — queue and flush on next onopen.
      outboundQueueRef.current.push(payload);
    }
  };

  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const sessionRef = useRef({ code: null, name: "Player 2" });
  const outboundQueueRef = useRef([]);  // payloads queued while WS was down

  const flushOutbound = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const q = outboundQueueRef.current;
    while (q.length) {
      const payload = q.shift();
      try { ws.send(JSON.stringify(payload)); } catch { q.unshift(payload); break; }
    }
  };

  const openSocket = () => {
    const c = sessionRef.current.code;
    const myName = sessionRef.current.name;
    const myAvatar = sessionRef.current.avatar || "";
    if (!c) return;
    const ws = new WebSocket(`${wsBase()}/ws/room/${c}/controller`);
    wsRef.current = ws;
    let pingTimer = null;

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0;
      ws.send(JSON.stringify({ type: "join", name: myName, avatar: myAvatar }));
      // Flush any chat messages that were queued while the WS was down.
      flushOutbound();
      setStatus("connected");
      setError(null);
      if (pingTimer) clearInterval(pingTimer);
      pingTimer = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          try { ws.send(JSON.stringify({ type: "ping", t: Date.now() })); } catch {}
        }
      }, 10000);
    };
    ws.onmessage = (ev) => {
      let m;
      try { m = JSON.parse(ev.data); } catch { return; }
      if (m.type === "ping" || m.type === "pong") return;
      if (m.type === "error") {
        // Real error from server (e.g., room_not_found) — stop reconnecting.
        reconnectAttemptsRef.current = 999;
        setError(m.error || "Connection error");
        setStatus("error");
        ws.close();
      }
      else if (m.type === "score") setScore({ p1: m.p1 ?? 0, p2: m.p2 ?? 0 });
      else if (m.type === "match_started") { setMatchStarted(true); setWinner(null); }
      else if (m.type === "game_over") {
        setMatchStarted(false);
        setWinner({ side: m.winner_side, p1: m.p1, p2: m.p2, p1_name: m.p1_name, p2_name: m.p2_name });
      } else if (m.type === "host_disconnected") {
        // Host left — keep our WS open and wait for them to come back.
      } else if (m.type === "host_info") {
        setHostName((m.name || "Player 1").slice(0, 20));
      } else if (m.type === "tick") {
        tickRef.current = m;
        // Reset prediction baseline whenever an authoritative snapshot arrives
        // (the snapshot already includes movement up to this moment, so any
        // further extrapolation should start fresh from `now`).
        lastTickAtRef.current = Date.now();
      } else if (m.type === "chat") {
        const txt = String(m.text || "");
        if (!txt) return;
        setChatMessages((prev) => [
          ...prev.slice(-199),
          { from: m.from || "host", name: (m.name || "Player 1").slice(0, 20), avatar: (m.avatar || "").slice(0, 60), text: txt, ts: Date.now() },
        ]);
      }
    };
    ws.onclose = () => {
      if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
      // Give up only on permanent errors.
      if (reconnectAttemptsRef.current >= 30) {
        setStatus("disconnected");
        return;
      }
      // Attempt reconnect with capped backoff.
      const delay = Math.min(5000, 500 * Math.pow(2, reconnectAttemptsRef.current));
      reconnectAttemptsRef.current += 1;
      setStatus("connecting");
      reconnectTimerRef.current = setTimeout(openSocket, delay);
    };
    ws.onerror = () => {};
  };

  const connect = async () => {
    const c = code.trim().toUpperCase();
    if (!/^[A-Z0-9]{3,8}$/.test(c)) { setError("Invalid code"); return; }
    const trimmedName = name.trim().slice(0, 20);
    if (!trimmedName) { setError("Enter a name"); return; }
    setError(null);
    // Verify the room exists BEFORE asking the user to pick an avatar.
    try {
      const meta = await getRoom(c);
      setRoomMeta(meta);
      if (meta.controller_connected) {
        setStatus("error");
        setError("Another controller is already connected to this room.");
        return;
      }
    } catch (e) {
      setStatus("error");
      setError(e?.response?.status === 404 ? "Room not found" : (e?.message || "Failed to verify room"));
      return;
    }
    sessionRef.current = {
      code: c,
      name: trimmedName,
      avatar: "",
    };
    reconnectAttemptsRef.current = 0;
    setStatus("connecting");
    openSocket();
  };

  useEffect(() => () => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    if (wsRef.current) wsRef.current.close();
  }, []);

  // Keep the bottom of the controller (chat input + send button) visible whenever a new
  // chat message arrives. Scroll the page (since outer container is overflow-x-hidden, the
  // body scrolls vertically) to the bottom anchor placed after the chat panel.
  useEffect(() => {
    if (status !== "connected" || matchStarted || winner) return;
    const id = requestAnimationFrame(() => {
      if (ctrlBottomRef.current && typeof ctrlBottomRef.current.scrollIntoView === "function") {
        ctrlBottomRef.current.scrollIntoView({ block: "end", inline: "nearest", behavior: "smooth" });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [chatMessages.length, status, matchStarted, winner]);

  // Keyboard arrows fallback
  useEffect(() => {
    if (status !== "connected") return;
    const isTyping = (e) => {
      const t = e.target;
      if (!t) return false;
      const tag = t.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || t.isContentEditable;
    };
    const down = (e) => {
      if (isTyping(e)) return;
      // Skip OS auto-repeat — we only need a single 'up'/'down' message per
      // press and a 'stop' on release. Sending one per repeat (~30/sec) was
      // saturating the WebSocket and causing the host paddle to lag.
      if (e.repeat) {
        if (["ArrowUp", "ArrowDown", "w", "W", "s", "S"].includes(e.key)) e.preventDefault();
        return;
      }
      if (e.key === "ArrowUp" || e.key === "w" || e.key === "W") { e.preventDefault(); sendInput("up"); }
      else if (e.key === "ArrowDown" || e.key === "s" || e.key === "S") { e.preventDefault(); sendInput("down"); }
    };
    const up = (e) => {
      if (isTyping(e)) return;
      if (["ArrowUp", "ArrowDown", "w", "W", "s", "S"].includes(e.key)) { e.preventDefault(); sendInput("stop"); }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [status]);

  // Orientation hint for narrow portrait phones
  useEffect(() => {
    const check = () => {
      const isTouch = (typeof window !== "undefined") &&
        (navigator.maxTouchPoints > 0 || "ontouchstart" in window);
      const portrait = window.innerHeight > window.innerWidth;
      const small = Math.min(window.innerWidth, window.innerHeight) < 500;
      setShowRotateHint(isTouch && portrait && small);
    };
    check();
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return () => {
      window.removeEventListener("resize", check);
      window.removeEventListener("orientationchange", check);
    };
  }, []);

  // Render mirrored canvas using tickRef
  useEffect(() => {
    if (status !== "connected") return;
    if (!matchStarted && !winner) return; // canvas only mounted during/after match
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const draw = () => {
      const t = tickRef.current;
      ctx.clearRect(0, 0, W, H);
      drawArenaBackground(ctx);

      // Crimson Knight (P1, left)
      drawPaddleMirror(ctx, 30, t.p1y, PADDLE_W, t.p1h, "#B22222", t.p1a);
      // Azure Knight (P2, right) — apply CLIENT-SIDE PREDICTION so the
      // local user's paddle visibly responds to keys/touches without the
      // full network RTT. We extrapolate from the latest authoritative
      // tick using the time elapsed since it was received and the local
      // input direction. Speed is approximated at 9.5 px/frame @ 60 fps
      // (~0.57 px/ms) — small mismatches converge on the next tick.
      const PRED_SPEED = 0.57; // px / ms
      const elapsed = Math.min(180, Date.now() - lastTickAtRef.current);
      const offset = localInputRef.current * PRED_SPEED * elapsed;
      const p2yPredicted = Math.max(0, Math.min(H - t.p2h, t.p2y + offset));
      drawPaddleMirror(ctx, W - 30 - PADDLE_W, p2yPredicted, PADDLE_W, t.p2h, "#1E3A8A", t.p2a);
      // Balls — lacquered wood, no glow
      const balls = Array.isArray(t.bs) ? t.bs : (t.bx != null ? [{ x: t.bx, y: t.by, s: 1 }] : []);
      for (const b of balls) {
        drawBall(ctx, b.x, b.y, BALL_R, b.s !== 0);
      }

      if (t.pu) {
        const def = POWERUP_DEFS[t.pu.t] || POWERUP_DEFS.speed;
        const pulse = 1 + Math.sin(Date.now() / 180) * 0.12;
        ctx.shadowColor = def.glow; ctx.shadowBlur = 24; ctx.fillStyle = def.fill;
        ctx.beginPath(); ctx.arc(t.pu.x, t.pu.y, POWERUP_R * pulse, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0;
        ctx.strokeStyle = "#E5C870"; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(t.pu.x, t.pu.y, POWERUP_R * pulse, 0, Math.PI * 2); ctx.stroke();
        drawPowerupIcon(ctx, t.pu.t, t.pu.x, t.pu.y);
      }
      ctx.shadowBlur = 0;

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [status, matchStarted, winner]);

  const PressButton = ({ action, children, testId, extraClass = "" }) => {
    const handlers = {
      onMouseDown: (e) => { e.preventDefault(); sendInput(action); },
      onMouseUp: () => sendInput("stop"),
      onMouseLeave: () => { if (lastDirRef.current === action) sendInput("stop"); },
      onTouchStart: (e) => { e.preventDefault(); sendInput(action); },
      onTouchEnd: (e) => { e.preventDefault(); sendInput("stop"); },
      onTouchCancel: () => sendInput("stop"),
    };
    return (
      <button
        {...handlers}
        className={`arcade-btn arcade-btn-magenta select-none w-full !text-2xl active:scale-95 active:shadow-[0_0_30px_rgba(255,0,255,0.7)] transition-transform ${extraClass}`}
        data-testid={testId}
        aria-label={action}
      >
        {children}
      </button>
    );
  };

  // Pre-connect form
  if (status === "idle" || status === "connecting" || status === "error") {
    return (
      <div className="wupong-stage relative min-h-screen overflow-x-clip wupong-bg" data-testid="controller-page">
        <div className="wupong-grain pointer-events-none absolute inset-0" />
        <TopBar
          theme="wupong"
          onBack={() => navigate("/pong-tournament")}
          backLabel="BACK"
          testId="controller-back-button"
        />
        <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row">
          <div
            className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r"
            style={{ borderColor: "rgba(192,132,252,0.18)" }}
          >
            <NavPane theme="wupong" />
          </div>
          <div className="flex-1 px-6 md:px-10 pt-8 pb-20 max-w-md">
          <div className="mb-8">
            <div className="wupong-stencil text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: "#C084FC" }}>
              Offense
            </div>
            <h1 className="wupong-headline text-5xl md:text-6xl" style={{ color: "#C084FC", textShadow: "3px 3px 0 #6D28D9" }}>
              ENTER YA CHAMBER
            </h1>
            <div className="wupong-tag" />
            <p className="mt-5 wupong-typewriter text-sm uppercase tracking-wider" style={{ color: "#E5C870", opacity: 0.85 }}>
              Get the chamber key from the host. Punch it in below.
            </p>
          </div>

          <div className="space-y-5 p-6" style={{ background: "rgba(26,24,20,0.85)", border: "2px solid #C084FC", boxShadow: "5px 5px 0 #6D28D9" }}>
            <div>
              <div className="wupong-mono text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: "#C084FC" }}>Chamber Key</div>
              <input
                className="w-full text-center text-3xl tracking-[0.6em] font-playfair"
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: "2px solid #C084FC",
                  color: "#C084FC",
                  outline: "none",
                  padding: "0.5rem 0",
                }}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
                placeholder="XXXX"
                inputMode="text"
                autoCapitalize="characters"
                maxLength={6}
                data-testid="controller-code-input"
              />
            </div>
            <div>
              <div className="wupong-mono text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: "#C084FC" }}>Your Name</div>
              <input
                className="w-full text-xl tracking-wider"
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: "2px solid #C084FC",
                  color: "#C084FC",
                  outline: "none",
                  padding: "0.5rem 0",
                  fontFamily: "'Playfair Display', serif",
                }}
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 20))}
                placeholder="Enter ya name"
                maxLength={20}
                data-testid="controller-name-input"
              />
            </div>
            {error && (
              <div className="wupong-mono text-sm" style={{ color: "#6D28D9" }} data-testid="controller-error">
                {error}
              </div>
            )}
            <button
              onClick={connect}
              disabled={status === "connecting"}
              className="wupong-cta w-full disabled:opacity-50"
              data-testid="controller-connect-button"
            >
              {status === "connecting" ? "APPROACHING…" : "TAKE THE FIELD"}
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  }

  // Active controller view
  return (
    <div className="relative min-h-screen overflow-x-clip flex flex-col" data-testid="controller-active">
      <div className="absolute inset-0 synthwave-grid pointer-events-none opacity-30" />

      {showRotateHint && (
        <div className="fixed inset-0 z-40 bg-black/95 flex items-center justify-center p-6 text-center" data-testid="rotate-hint-overlay">
          <div className="arcade-panel p-8 max-w-sm">
            <Smartphone className="mx-auto text-blue-300 mb-3 neon-pulse rotate-90" size={42} />
            <div className="font-display text-xl text-glow-magenta uppercase tracking-widest mb-2">
              Rotate to Landscape
            </div>
            <p className="text-sm text-gray-300 font-body">
              Turn your device sideways for the full mirrored arena view.
            </p>
          </div>
        </div>
      )}

      <div className="relative z-10 w-full max-w-5xl mx-auto px-3 pt-3 pb-3 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => { if (wsRef.current) wsRef.current.close(); navigate("/"); }}
            className="arcade-btn arcade-btn-ghost !px-3 !py-2 text-xs"
            data-testid="controller-leave-button"
          >
            <ArrowLeft size={14} /> Leave
          </button>
          <div className="flex items-center gap-2 text-xs font-mono">
            {status === "connected" ? (
              <span className="text-emerald-300 flex items-center gap-1" data-testid="controller-status-connected">
                <Wifi size={14} /> On the field · {code}
              </span>
            ) : (
              <span className="text-rose-400 flex items-center gap-1" data-testid="controller-status-disconnected">
                <WifiOff size={14} /> {error || "Withdrawn"}
              </span>
            )}
          </div>
        </div>

        {status !== "connected" && (
          <div className="mb-3 arcade-panel px-4 py-3 text-center" data-testid="controller-reconnect-banner">
            <div className="text-red-700 font-display text-xs tracking-[0.3em] uppercase mb-2">
              The raven hath strayed
            </div>
            <button
              onClick={() => { setStatus("idle"); setError(null); setChatMessages([]); }}
              className="arcade-btn arcade-btn-magenta !px-5 !py-2 text-xs"
              data-testid="controller-reconnect-button"
            >
              Re-mount
            </button>
          </div>
        )}

        {/* Awaiting host: shown after connect but before host hits "Sound the Horn". */}
        {status === "connected" && !matchStarted && !winner && (
          <div className="mb-3 arcade-panel px-4 py-4 text-center neon-pulse" data-testid="controller-awaiting-host-banner">
            <div className="text-xs tracking-[0.4em] text-amber-700 font-display uppercase mb-1">
              Awaiting Host
            </div>
            <div className="text-stone-700 italic font-body text-sm">
              The host is preparing the lists. The match begins when they sound the horn.
            </div>
          </div>
        )}

        {/* Score */}
        <div className="grid grid-cols-3 items-end gap-2 mb-2">
          <div className="text-left">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-red-400 font-display truncate">
                {hostName || roomMeta?.p1_name || "Player 1"}
              </div>
              <div className="font-mono font-bold text-3xl text-glow-cyan" data-testid="controller-p1-score">
                {score.p1}
              </div>
            </div>
          </div>
          <div className="text-center text-amber-300/70 font-display tracking-[0.3em] uppercase text-[10px] pb-2">vs.</div>
          <div className="text-right">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-blue-400 font-display truncate">
                {name || "Player 2"}
              </div>
              <div className="font-mono font-bold text-3xl text-glow-magenta" data-testid="controller-p2-score">
                {score.p2}
              </div>
            </div>
          </div>
        </div>

        {winner && (
          <div className="arcade-panel p-3 text-center mb-2" data-testid="controller-winner-panel">
            <Trophy className="mx-auto text-yellow-300 mb-1 neon-pulse" size={28} />
            <div className="text-xs tracking-[0.4em] uppercase text-red-400 font-display">
              {winner.side === "p2" ? "Victory!" : "Defeat"}
            </div>
            <div className="font-mono text-lg text-white mt-1">{winner.p1} – {winner.p2}</div>
          </div>
        )}

        {/* Mirrored canvas — only during/after match. During chat phase the canvas is empty anyway,
            and removing it gives the chat panel full vertical space so the send button stays reachable. */}
        {(matchStarted || winner) && (
          <div
            className="canvas-wrap scanlines crt-flicker w-full mb-3"
            style={{ aspectRatio: `${W} / ${H}` }}
            data-testid="controller-canvas-wrap"
          >
            <canvas
              ref={canvasRef}
              width={W}
              height={H}
              className="block w-full h-full"
              data-testid="controller-canvas"
            />
          </div>
        )}

        {/* Chat (pre-game) OR Buttons (in-match) */}
        {!matchStarted && !winner ? (
          <ChatPanel
            messages={chatMessages}
            onSend={sendChat}
            selfRole="controller"
            selfName={name}
            testIdPrefix="controller-chat"
            className="flex-1 min-h-[260px]"
          />
        ) : (
          <div className="grid grid-cols-2 gap-3 flex-1 min-h-[120px]" data-testid="controller-buttons-row">
            <PressButton action="up" testId="controller-up-button" extraClass="!py-6 h-full">
              <ArrowUp size={40} />
            </PressButton>
            <PressButton action="down" testId="controller-down-button" extraClass="!py-6 h-full">
              <ArrowDown size={40} />
            </PressButton>
          </div>
        )}

        <div className="mt-2 mb-16 text-center text-[10px] text-amber-200/60 font-body italic tracking-widest">
          {!matchStarted && !winner ? "The host shall sound the horn — parley until then" : "Hold to move · W/S or ↑/↓ also serve"}
        </div>

        <div ref={ctrlBottomRef} aria-hidden="true" className="h-1" />
      </div>
    </div>
  );
};

export default Controller;
